import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  TextField,
} from '@material-ui/core';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { DSText } from '@engenharia/ds-react';

import Logo from '../../components/molecules/Logo';
import Notification from '../../components/Notification';
import MetaHeader from '../../components/MetaHeader';
import { Container, Card, FormContainer, FormControlInput } from './styles';
import { confirmPasswordValidation, strongPasswordValidation } from '../../utils/validations';
import api from '../../services/api';
import { getLanguage, logout } from '../../services/auth';
import Button from '../../components/atoms/Button';
import Header from '../../components/Header';
import { transcodeLanguage } from '../../utils/language';
import FormHelperTextValidation from '../../components/FormHelperValidation';

export default function ResetPassword() {
  const history = useHistory();
  const { t } = useTranslation();

  const [state, setState] = React.useState({
    newPassword: '',
    confirmPassword: '',
    invalidPassword: {},
    invalidConfirmation: false,
    showNewPassword: false,
    showConfirmNewPassword: false,
  });

  const [notification, setNotification] = useState({});

  useEffect(() => {
    logout();
  }, []);

  const handleClickShowNewPassword = () => {
    setState({ ...state, showNewPassword: !state.showNewPassword });
  };

  const handleClickShowConfirmNewPassword = () => {
    setState({ ...state, showConfirmNewPassword: !state.showConfirmNewPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleNewPasswordChange = (event) => {
    setState({
      ...state,
      newPassword: event.target.value,
      invalidPassword: strongPasswordValidation(event.target.value),
      invalidConfirmation: state.confirmPassword
        ? confirmPasswordValidation(state.confirmPassword, event.target.value)
        : state.confirmPassword,
    });
  };

  const handleConfirmNPasswordChange = (event) => {
    setState({
      ...state,
      confirmPassword: event.target.value,
      invalidConfirmation: confirmPasswordValidation(event.target.value, state.newPassword),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { newPassword, confirmPassword } = state;
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const resetPasswordToken = params.get('reset_password_token');

    try {
      if (
        !newPassword ||
        !confirmPassword ||
        state.invalidPassword?.passwordInvalidError ||
        state.invalidConfirmation
      ) {
        setNotification({ key: new Date().getTime(), message: t('errors.reset-password-invalid') });
        return;
      }

      const response = await api.put('accounts/reset_password', {
        token: resetPasswordToken,
        password: newPassword,
        password_confirmation: confirmPassword,
        language: transcodeLanguage(getLanguage()),
      });

      setNotification({ key: new Date().getTime(), message: response?.data?.message });

      setTimeout(() => {
        history.push('/accounts/sign_in');
      }, 2000);
    } catch (error) {
      const { response } = error;
      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  return (
    <>
      <Header positionFixed />
      <Container>
        <MetaHeader
          title="Resetar minha senha - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes Sociais | Resetar minha senha"
        />
        <Logo />
        <Box mx={2} my={8} component={Card}>
          <DSText variant="h3" color="primary">
            {t('resetPassword.title')}
          </DSText>
          <FormContainer>
            <form onSubmit={handleSubmit}>
              <FormControlInput
                variant="outlined"
                error={state.invalidPassword?.passwordInvalidError}
                fullWidth
              >
                <TextField
                  label={`${t('resetPassword.input.newPassword')} *`}
                  type={state.showNewPassword ? 'text' : 'password'}
                  error={state.invalidPassword?.passwordInvalidError}
                  fullWidth
                  required
                  data-testid="formNewPassword"
                  id="newPassword"
                  value={state.newPassword}
                  onChange={handleNewPasswordChange}
                  variant="outlined"
                  margin="normal"
                  autoFocus
                  endAdornment={
                    <InputAdornment>
                      <IconButton
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {state.showNewPassword ? <MdVisibility /> : <MdVisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {(state.newPassword?.length || state.invalidPassword?.passwordInvalidError) && (
                  <FormHelperTextValidation validation={state.invalidPassword} />
                )}
              </FormControlInput>

              <FormControlInput variant="outlined" error={state.invalidConfirmation} fullWidth>
                <TextField
                  label={`${t('resetPassword.input.confirmNewPassword')} *`}
                  type={state.showConfirmNewPassword ? 'text' : 'password'}
                  error={state.invalidConfirmation}
                  fullWidth
                  required
                  data-testid="formConfirmPassword"
                  id="confirmNewPassword"
                  value={state.confirmPassword}
                  onChange={handleConfirmNPasswordChange}
                  variant="outlined"
                  margin="normal"
                  endAdornment={
                    <InputAdornment>
                      <IconButton
                        onClick={handleClickShowConfirmNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {state.showConfirmNewPassword ? <MdVisibility /> : <MdVisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {state.invalidConfirmation && (
                  <FormHelperText error id="email-error">
                    {t('resetPassword.input.invalidConfirmation')}
                  </FormHelperText>
                )}
              </FormControlInput>

              <FormControl fullWidth>
                <Button mt={4} type="submit" fullWidth color="primary" data-testid="formSubmit">
                  {t('resetPassword.button')}
                </Button>
              </FormControl>
            </form>
          </FormContainer>
        </Box>
        {notification?.key && (
          <Notification
            key={notification.key}
            index={notification.key}
            message={notification.message}
          />
        )}
      </Container>
    </>
  );
}
